import React, { useCallback, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { Text } from '@hub/text'
import useForm, {
  IFormErrors,
  IFormValues,
} from '@scentregroup/shared/hooks/use-form'
import { FormInput } from '@scentregroup/shared/hub-components/form'

import { UpdateCustomerDetails } from '../../graphql/mutations'
import { useSafeMutation } from '../../lib/apollo-client'
import { userAtom } from '../../lib/atoms/user-info'
import {
  emptyErrorMessage,
  getValidationRequiredFunction,
} from '../../lib/form'
import { onError } from '../../lib/on-error'
import useNotification from '../../lib/use-notification'
import { useProfileEdit } from '../../lib/use-profile-edit'
import { ProfileSection } from '../../types/profile'
import ProfileLayout from './profile-layout'
import { ERROR_UPDATE_NAME } from '../../lib/error-messages'
import { MESSAGE_NAME_UPDATED } from '../../lib/notification-messages'

type Name = {
  firstName: string
  lastName: string
}

type ProfileNameErrors = IFormErrors<Name>
type ProfileNameValues = IFormValues<Name>

enum ProfileNameFields {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
}

const ProfileName: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { openNotification } = useNotification()
  const [updateCustomerDetails] = useSafeMutation(UpdateCustomerDetails)
  const { data: user, refetch } = useRecoilValue(userAtom)
  const [isSaving, setIsSaving] = useState(false)
  const { cancelEdit } = useProfileEdit(ProfileSection.NAME)

  const firstName = user?.firstName
  const lastName = user?.lastName

  const defaultProfileName = {
    firstName,
    lastName,
  }

  const validateProfileName = (
    values: ProfileNameValues,
    isSubmitting?: boolean
  ): ProfileNameErrors => {
    const errors: ProfileNameErrors = {}
    const isTouched = getValidationRequiredFunction(values, isSubmitting)

    const validateEmptyWhenTouched = (
      field: ProfileNameFields,
      errorMessageName: string
    ): void => {
      if (isTouched(field)) {
        errors[field] = emptyErrorMessage(values[field], errorMessageName)
      }
    }

    validateEmptyWhenTouched(ProfileNameFields.FIRST_NAME, 'valid first name')
    validateEmptyWhenTouched(ProfileNameFields.LAST_NAME, 'valid last name')

    return errors
  }

  const onSave = useCallback(
    async (values: Name) => {
      setIsSaving(true)

      try {
        await updateCustomerDetails({
          variables: {
            input: {
              firstName: values.firstName,
              lastName: values.lastName,
            },
          },
        })

        if (refetch) await refetch()

        cancelEdit()
        openNotification(MESSAGE_NAME_UPDATED)
      } catch (error) {
        onError(error, openNotification, ERROR_UPDATE_NAME)
      } finally {
        setIsSaving(false)
      }
    },
    [updateCustomerDetails, cancelEdit, openNotification, refetch]
  )

  const {
    errors,
    values,
    hasChanges,
    resetValues,
    clearErrors,
    handleSubmit,
    handleChange,
    handleBlur,
  } = useForm<Name>(validateProfileName, onSave, defaultProfileName)

  const onCancel = useCallback(() => {
    resetValues([ProfileNameFields.FIRST_NAME, ProfileNameFields.LAST_NAME])
    clearErrors()
  }, [resetValues, clearErrors])

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void => {
    clearErrors()
    handleChange({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })
  }

  const onBlur = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ): void =>
    handleBlur({
      name: e.currentTarget.name,
      value: e.currentTarget.value,
    })

  return (
    <ProfileLayout
      section={ProfileSection.NAME}
      title={'Name'}
      readOnlyView={<Text>{`${firstName} ${lastName}`}</Text>}
      handleSubmit={handleSubmit}
      handleCancel={onCancel}
      hasChanges={hasChanges}
      isSubmitting={isSaving}
    >
      <FormInput
        id={'first-name'}
        name={ProfileNameFields.FIRST_NAME}
        label={'First name'}
        value={values.firstName || ''}
        autoComplete={'given-name'}
        autoCorrect={'off'}
        isInvalid={!!errors.firstName}
        errorMessage={errors.firstName}
        isLoading={false}
        onChange={onChange}
        onBlur={onBlur}
      />
      <FormInput
        id={'last-name'}
        name={ProfileNameFields.LAST_NAME}
        label={'Last name'}
        value={values.lastName || ''}
        autoComplete={'family-name'}
        autoCorrect={'off'}
        isInvalid={!!errors.lastName}
        errorMessage={errors.lastName}
        isLoading={false}
        onChange={onChange}
        onBlur={onBlur}
      />
    </ProfileLayout>
  )
}

export default ProfileName
